import { ReactNode, useState } from 'react';
import { useIntl } from 'react-intl';
import { Redirect } from 'react-router';
import { DeleteConfirmationDialog } from '@rossum/components/delete-confirmation-dialog/DeleteConfirmationDialog';
import { PageLayout } from '@rossum/components/page-layout/PageLayout';
import { Button } from '@rossum/ui/material';
import { notify } from '../../toaster';
import {
  ReplaceDataDialog,
  ReplaceDataVariant,
} from '../dataset-import/ReplaceDataDialog';
import { DatasetsHeader } from '../header';
import { useDeleteDataset } from '../hooks/useDeleteDataset';
import {
  DatasetExportError,
  DownloadFormat,
  useExportDataset,
} from '../hooks/useExportDataset';
import { useGetDatasetRowCount } from '../hooks/useGetDatasetRowCount';
import { isOperationInProgress, useOperations } from '../hooks/useOperations';
import { datasetsPath, datasetTablePath } from '../routes';
import { Dataset } from '../types';
import { DatasetActionsMenu } from './DatasetActionsMenu';

export type DatasetTab = 'data' | 'settings' | 'logs';

type DatasetDetailLayoutProps = {
  table: Dataset;
  tab: DatasetTab;
  // The tab's own save button, rendered after the actions menu and disabled
  // while an operation runs. It submits `form` when given.
  save?: {
    label: string;
    dataCy?: string;
    disabled: boolean;
    loading: boolean;
    onClick?: () => void;
    form?: string;
  };
  // Unsaved changes on the tab block the actions that would overwrite or
  // discard them.
  hasPendingChanges?: boolean;
  fullWidth?: boolean;
  children: ReactNode;
};

// Shared shell of the dataset tabs: header, tabs, the actions menu and the
// dialogs it opens.
export const DatasetDetailLayout = ({
  table,
  tab,
  save,
  hasPendingChanges = false,
  fullWidth,
  children,
}: DatasetDetailLayoutProps) => {
  const intl = useIntl();
  const [replaceDataVariant, setReplaceDataVariant] =
    useState<ReplaceDataVariant | null>(null);
  const [deletingDataset, setDeletingDataset] = useState(false);
  const { data: operations, isFetching: isFetchingOperations } = useOperations({
    datasetName: table.name,
    // The Logs tab polls the full list anyway; share it instead of polling
    // the latest operation separately.
    limit: tab === 'logs' ? undefined : 1,
  });
  const lastOperation = operations?.[0];
  const operationInProgress = isOperationInProgress(lastOperation);

  const { data: rowCount } = useGetDatasetRowCount({
    name: table.name,
    find: {},
    lastSuccessOperation:
      lastOperation?.status === 'FINISHED' ? lastOperation : undefined,
  });
  // Unknown counts as having rows, so a loading count never hides the replace
  // warning.
  const hasRows = rowCount !== 0;

  const { mutate: exportDataset, isLoading: isExporting } = useExportDataset();

  const { mutate: deleteDataset, isLoading: isDeletingDataset } =
    useDeleteDataset();

  const handleExport = (format: DownloadFormat) =>
    exportDataset(
      { dataset: table.name, format },
      {
        onError: error => {
          notify.error({
            title:
              error instanceof DatasetExportError && error.message
                ? error.message
                : intl.formatMessage({
                    id: 'features.datasets.notification.exportFailed',
                  }),
          });
        },
      }
    );

  const canDelete = !operationInProgress && !hasPendingChanges;
  // Only manually-uploaded datasets are editable from the UI.
  const canReplace = canDelete && table.metadata.manual;
  const canExport = canDelete && !isExporting && !!rowCount;

  // The backend drops a dataset as soon as its delete is accepted, whoever
  // started it, so leave without waiting for the operation to end. Cached
  // operations may predate a dataset re-created under the same name.
  if (!isFetchingOperations && lastOperation?.type === 'delete') {
    return <Redirect to={datasetsPath()} />;
  }

  return (
    <PageLayout
      fullWidth={fullWidth}
      renderHeader={props => (
        <DatasetsHeader
          {...props}
          datasetId={table.name}
          datasetTab={tab}
          title={table.metadata.name}
          description={table.metadata.description}
          breadcrumbs={[
            {
              label: table.metadata.name,
              to: datasetTablePath(table.name),
            },
            {
              label: intl.formatMessage({
                id: `features.datasets.${tab}.title`,
              }),
            },
          ]}
          buttons={[
            <DatasetActionsMenu
              key="actions"
              hasRows={hasRows}
              exporting={isExporting}
              onExport={canExport ? handleExport : undefined}
              onReplace={
                canReplace
                  ? () => setReplaceDataVariant(hasRows ? 'replace' : 'upload')
                  : undefined
              }
              onDelete={canDelete ? () => setDeletingDataset(true) : undefined}
            />,
            ...(save
              ? [
                  <Button
                    key="save"
                    data-cy={save.dataCy}
                    variant="contained"
                    type={save.form ? 'submit' : 'button'}
                    form={save.form}
                    loading={save.loading}
                    disabled={save.disabled || operationInProgress}
                    onClick={save.onClick}
                  >
                    {save.label}
                  </Button>,
                ]
              : []),
          ]}
        />
      )}
    >
      {children}
      <ReplaceDataDialog
        variant={replaceDataVariant}
        onClose={() => setReplaceDataVariant(null)}
        table={table}
      />
      <DeleteConfirmationDialog
        open={deletingDataset}
        title={intl.formatMessage(
          {
            id: 'features.datasets.settings.dialog.title',
          },
          { name: table.metadata.name }
        )}
        onConfirm={() => {
          deleteDataset(
            { datasetName: table.name },
            {
              onSuccess: () => {
                setDeletingDataset(false);
                notify.default({
                  title: intl.formatMessage({
                    id: 'features.datasets.notification.deleteStarted',
                  }),
                });
              },
            }
          );
        }}
        onCancel={() => setDeletingDataset(false)}
        description={intl.formatMessage({
          id: 'features.datasets.settings.dialog.text',
        })}
        isLoading={isDeletingDataset}
      />
    </PageLayout>
  );
};
